"use client";
import { addDays, addYears, isSameDay, isThisYear } from "date-fns";
import Button from 'src/components/button/Button';
import ChartTypeLabel from 'src/components/kinotoppen/ChartTypeLabel';
import MovieChartSelector from 'src/components/kinotoppen/MovieChartTypeSelector';
import { getMovieChartText } from 'src/components/kinotoppen/kinotoppenHelpers';
import LoadingIndicator from 'src/components/loadingindicator/LoadingIndicator';
import useMovieChart, { showNextButton } from 'src/lib/client/useMovieChart';
import { MovieChart } from 'src/lib/movieinfo/movieinfotypes';
import styles from "./KinotoppenLoader.module.css";
import { useCallback } from "react";

//#region [Props]
type KinotoppenLoaderProps = {
    children: (movieChart: MovieChart | undefined | null, showAllMovies: boolean) => React.ReactNode;
};
//#endregion

//#region [Component]
export default function KinotoppenLoader({ children }: KinotoppenLoaderProps) {
    const {
        isLoading: isMovieChartLoading,
        data: movieChart,
        date,
        chartType,
        showAllMovies,
        changeDate,
        setChartType,
        setShowAllMovies
    } = useMovieChart();

    let dateFrom: string = "";
    let dateTo: string = "";

    if (movieChart) {
        dateFrom = movieChart.periodStart.substring(8, 10) + "." + movieChart.periodStart.substring(5, 7);
        dateTo = movieChart.periodEnd.substring(8, 10) + "." + movieChart.periodEnd.substring(5, 7) + "." + movieChart.periodEnd.substring(0, 4);
    }

    const changePeriodByYear = chartType === "year" || chartType === "opening";

    const onClick = useCallback((direction: string) => {
        if (direction === "prev") {
            if (changePeriodByYear) {
                changeDate(addYears(date, -1));
            }
            else {
                changeDate(addDays(date, -7));
            }
        }
        else // next
        {
            if (changePeriodByYear) {
                changeDate(addYears(date, 1));
            }
            else {
                changeDate(addDays(date, 7));
            }
        }
    }, [date, changeDate, changePeriodByYear]);

    const shouldShowNextButton = showNextButton(date, chartType);

    return <>

        <div className={styles.ControlsContainer}>
            <MovieChartSelector chartType={chartType} setChartType={setChartType} />
            {isMovieChartLoading && <LoadingIndicator position='InlineCenter' />}
            {!isMovieChartLoading && <>
                <div className={styles.ChartTypeHeader}>
                    <ChartTypeLabel chartType={chartType} /> {dateFrom} - {dateTo}
                </div>
                <button className={styles.NextPrevButton} onClick={() => onClick("prev")}>Forrige {getMovieChartText(chartType)}</button>
                {shouldShowNextButton && <button className={styles.NextPrevButton} onClick={() => onClick("next")}>Neste {getMovieChartText(chartType)}</button>}
            </>}
        </div>

        {isMovieChartLoading && <LoadingIndicator size='large' position='AbsoluteCenter' />}

        {children(movieChart, showAllMovies)}

        {!showAllMovies && <div className={styles.SeeAllButtonContainer}>
            <Button text='Se hele listen' onClick={() => setShowAllMovies(true)} />
        </div>}

    </>;
}
//#endregion
